import { useMemo, useRef, useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
//
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Iconify, Label } from '@/components';
import { VBMCardWrapper, VBMCardTitle } from '..';
//
import {
  getArrivalRiskPresentation,
  returnStatusObject,
} from '@/features/bookings-management/utils';
import { getSettingsData } from '@/features/app/api';
import { deleteBookingCleanerSessionStatusHistory } from '@/features/bookings-management/api';
import useAuth from '@/hooks/useAuth';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { useGetAccess } from '@/hooks/useAccess';
//
import { LabelColors, StatusWithSyleClass } from '@/types';
import {
  BookingCleanerSessionStatusHistory,
  BookingSessionStatusHistory,
} from '@/features/bookings-management/types';
import { UserAddress } from '@/features/users/shared/types';
import {
  DEFAULT_LEAFLET_CENTER,
  ensureLeafletAssets,
} from '@/utils/leafletLoader';
import { Theme, useTheme } from '@mui/material/styles';

type StatusHistoryProps = {
  statusHistory: BookingSessionStatusHistory[];
  bookingCleanerSessionStatusHistory?: BookingCleanerSessionStatusHistory[];
  bookingAddress?: UserAddress | null;
};

type StatusHistoryMapDialogProps = {
  open: boolean;
  onClose: () => void;
  bookingAddress?: UserAddress | null;
  statusHistory: BookingSessionStatusHistory[];
  bookingSessionStatuses?: StatusWithSyleClass[];
};

export default function VBMStatusHistory({
  statusHistory,
  bookingCleanerSessionStatusHistory = [],
  bookingAddress = null,
}: StatusHistoryProps) {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuth();
  const { canDelete } = useGetAccess('bookings');
  const { errorMsg, successMsg } = useSnackbarMsg();
  const [historyEntryToDelete, setHistoryEntryToDelete] = useState<{
    entry: BookingSessionStatusHistory;
    statusHistoryId: number;
  } | null>(null);
  const { data: bookingSessionStatuses } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettingsData,
    select: (data) => data.bookingSessionStatuses,
    refetchOnWindowFocus: false,
  });

  const [isMapOpen, setIsMapOpen] = useState(false);
  const bookingAddressHasCoords = Boolean(
    bookingAddress?.latitude && bookingAddress?.longitude
  );
  const bookingCoordinates = useMemo(() => {
    if (!bookingAddressHasCoords) return null;
    const lat = parseCoordinate(bookingAddress?.latitude);
    const lng = parseCoordinate(bookingAddress?.longitude);
    if (lat === null || lng === null) return null;
    return { lat, lng };
  }, [bookingAddressHasCoords, bookingAddress]);
  const hasHistoryCoords = useMemo(
    () =>
      statusHistory.some(
        (entry) => Boolean(entry.latitude) && Boolean(entry.longitude)
      ),
    [statusHistory]
  );
  const canShowMap = bookingAddressHasCoords || hasHistoryCoords;

  const deleteStatusHistoryMutation = useMutation({
    mutationFn: (statusHistoryId: number) =>
      deleteBookingCleanerSessionStatusHistory(statusHistoryId),
    onSuccess: () => {
      successMsg('Status history record deleted');
      setHistoryEntryToDelete(null);
      queryClient.invalidateQueries(['bookingSession']);
      queryClient.invalidateQueries(['bookingSessions']);
    },
    onError: (error) => {
      errorMsg(error, 'Something went wrong while deleting status history');
    },
  });

  const handleConfirmDelete = () => {
    if (!historyEntryToDelete?.statusHistoryId) return;
    deleteStatusHistoryMutation.mutate(historyEntryToDelete.statusHistoryId);
  };
  const canManageStatusHistory = Boolean(isSuperAdmin || canDelete);

  return (
    <VBMCardWrapper>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <VBMCardTitle title="Status history" />
        <Tooltip
          title={
            canShowMap
              ? 'View booking and status locations on map'
              : 'No coordinates available'
          }
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Iconify icon="eva:map-outline" />}
              disabled={!canShowMap}
              onClick={() => setIsMapOpen(true)}
            >
              View map
            </Button>
          </span>
        </Tooltip>
      </Stack>
      <List
        sx={{
          overflow: 'auto',
          maxHeight: 200,
          backgroundColor: 'background.paper',
          borderRadius: 1,
          py: 0,
        }}
      >
        <>
          {statusHistory.map((el, index) => {
            const statusHistoryId = getDeletableStatusHistoryId(
              el,
              bookingCleanerSessionStatusHistory
            );
            const canDeleteEntry = Boolean(
              canManageStatusHistory && statusHistoryId
            );
            const entryLat = parseCoordinate(el.latitude);
            const entryLng = parseCoordinate(el.longitude);
            const hasEntryCoords =
              entryLat !== null && entryLng !== null && !Number.isNaN(entryLat);
            const distanceLabel =
              hasEntryCoords && bookingCoordinates
                ? formatDistanceKm(
                    haversineDistanceKm(
                      bookingCoordinates.lat,
                      bookingCoordinates.lng,
                      entryLat as number,
                      entryLng as number
                    )
                  )
                : null;
            const arrivalRisk = getArrivalRiskPresentation(el.arrival_risk);

            return (
              <Stack
                component={ListItem}
                key={el.id ?? el.created_at + el.status}
                sx={{
                  backgroundColor: !!(index % 2)
                    ? 'background.paper'
                    : 'background.neutral',
                }}
              >
                <Grid container spacing={1} alignItems="center">
                  {/* CREATED AT */}
                  <Grid item container alignSelf="flex-start" xs={3}>
                    <Stack direction="row" spacing={0.5}>
                      <Typography
                        fontSize={12}
                        fontWeight={500}
                        textAlign="left"
                      >
                        {el.created_at}
                      </Typography>
                    </Stack>
                  </Grid>

                  {/* STATUS LABEL */}
                  <Grid item container alignSelf="flex-start" xs={2.5}>
                    <Stack spacing={0.5} alignItems="flex-start">
                      <Label
                        color={
                          returnStatusObject(
                            el.status,
                            bookingSessionStatuses || []
                          ).style_class as LabelColors
                        }
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        {
                          returnStatusObject(
                            el.status,
                            bookingSessionStatuses || []
                          ).display
                        }
                      </Label>
                      {arrivalRisk && (
                        <Label
                          color={arrivalRisk.color}
                          variant={arrivalRisk.variant}
                          sx={{
                            height: 'auto',
                            lineHeight: 1.4,
                            maxWidth: '100%',
                            py: 0.25,
                            textAlign: 'left',
                            whiteSpace: 'normal',
                          }}
                        >
                          {arrivalRisk.text}
                        </Label>
                      )}
                    </Stack>
                  </Grid>

                  {/* USER CHIP */}
                  <Grid
                    item
                    container
                    alignSelf="flex-start"
                    xs={3.5}
                    sx={{ minWidth: 0 }}
                  >
                    <Chip
                      size="small"
                      avatar={
                        <Avatar alt={el.full_name} src={el.profile_image} />
                      }
                      label={
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{
                            minWidth: 0,
                            mr: el.is_admin ? -0.5 : 'inherit',
                          }}
                        >
                          <Typography fontSize={12} noWrap>
                            {el.full_name}.
                          </Typography>
                          {el.is_admin && (
                            <Iconify
                              icon="mdi:shield-star-outline"
                              width={15}
                              color="info.main"
                            />
                          )}
                        </Stack>
                      }
                      variant="outlined"
                      sx={{
                        maxWidth: 1,
                        '& .MuiChip-label': {
                          minWidth: 0,
                        },
                      }}
                    />
                  </Grid>

                  {/* DISTANCE */}
                  <Grid item container alignSelf="flex-start" xs={2}>
                    {distanceLabel && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 500,
                          lineHeight: 1.35,
                          textAlign: 'right',
                          width: 1,
                        }}
                      >
                        {distanceLabel} from property
                      </Typography>
                    )}
                  </Grid>

                  {/* ACTIONS */}
                  <Grid
                    item
                    container
                    alignSelf="flex-start"
                    justifyContent="flex-end"
                    xs={1}
                  >
                    {canManageStatusHistory && (
                      <Tooltip
                        title={
                          canDeleteEntry
                            ? 'Delete status history record'
                            : 'This status history record cannot be deleted'
                        }
                      >
                        <span>
                          <IconButton
                            aria-label={
                              statusHistoryId
                                ? `Delete status history record ${statusHistoryId}`
                                : `Delete status history record from ${el.created_at}`
                            }
                            color="error"
                            size="small"
                            onClick={() => {
                              if (!statusHistoryId) return;
                              setHistoryEntryToDelete({
                                entry: el,
                                statusHistoryId,
                              });
                            }}
                            disabled={
                              !statusHistoryId ||
                              (deleteStatusHistoryMutation.isLoading &&
                                deleteStatusHistoryMutation.variables ===
                                  statusHistoryId)
                            }
                            sx={{ mt: -0.5 }}
                          >
                            <Iconify icon="eva:trash-2-outline" width={16} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Grid>
                </Grid>

                {/* EXTRA NOTES IN CASE OF REVIEW */}
                {el.show_extra_notes && (
                  <Box
                    component={Typography}
                    fontSize={13}
                    fontStyle="italic"
                    sx={{ alignSelf: 'start', pt: 0.5 }}
                  >
                    This job has been flagged for review. Contact the
                    provider/client.
                  </Box>
                )}
              </Stack>
            );
          })}
        </>
      </List>
      <Dialog
        open={Boolean(historyEntryToDelete)}
        onClose={() => setHistoryEntryToDelete(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete status history record?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the {historyEntryToDelete?.entry.status} record
            from {historyEntryToDelete?.entry.created_at}.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => setHistoryEntryToDelete(null)}
            disabled={deleteStatusHistoryMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleteStatusHistoryMutation.isLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {isMapOpen && (
        <StatusHistoryMapDialog
          open={isMapOpen}
          onClose={() => setIsMapOpen(false)}
          bookingAddress={bookingAddress}
          statusHistory={statusHistory}
          bookingSessionStatuses={bookingSessionStatuses || []}
        />
      )}
    </VBMCardWrapper>
  );
}

const normalizeHistoryStatus = (status?: string | null) =>
  (status ?? '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const historyStatusesMatch = (
  displayStatus?: string | null,
  rawStatus?: string | null
) => {
  const normalizedDisplayStatus = normalizeHistoryStatus(displayStatus);
  const normalizedRawStatus = normalizeHistoryStatus(rawStatus);

  if (!normalizedDisplayStatus || !normalizedRawStatus) {
    return normalizedDisplayStatus === normalizedRawStatus;
  }

  return (
    normalizedDisplayStatus === normalizedRawStatus ||
    normalizedDisplayStatus.includes(normalizedRawStatus) ||
    normalizedRawStatus.includes(normalizedDisplayStatus)
  );
};

const historyValuesMatch = (
  displayValue?: string | number | null,
  rawValue?: string | number | null
) => {
  if (displayValue === undefined || displayValue === null) return true;
  if (rawValue === undefined || rawValue === null) return true;
  return String(displayValue) === String(rawValue);
};

const getDeletableStatusHistoryId = (
  entry: BookingSessionStatusHistory,
  cleanerHistory: BookingCleanerSessionStatusHistory[]
) => {
  if (entry.id) return entry.id;

  const matchingHistory = cleanerHistory.find(
    (history) =>
      Boolean(history.id) &&
      history.created_at === entry.created_at &&
      historyStatusesMatch(entry.status, history.status) &&
      historyValuesMatch(
        entry.booking_session_id,
        history.booking_session_id
      ) &&
      historyValuesMatch(entry.booking_cleaner_id, history.booking_cleaner_id)
  );

  return matchingHistory?.id ?? null;
};

const parseCoordinate = (value?: string | number | null) => {
  const numeric =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
      ? parseFloat(value)
      : NaN;
  return Number.isFinite(numeric) ? numeric : null;
};

type StatusHistoryColorFields = BookingSessionStatusHistory & {
  color?: string | null;
  status_color?: string | null;
  style_class?: string | null;
};

const STATUS_LABEL_COLORS: LabelColors[] = [
  'default',
  'error',
  'info',
  'primary',
  'secondary',
  'success',
  'warning',
];

const normalizeLabelColor = (value?: string | null): LabelColors => {
  const normalizedValue = value?.trim().toLowerCase();
  return STATUS_LABEL_COLORS.includes(normalizedValue as LabelColors)
    ? (normalizedValue as LabelColors)
    : 'default';
};

const getThemeStatusColor = (color: LabelColors, theme: Theme) => {
  if (color === 'default') return theme.palette.grey[500];
  return theme.palette[color].main;
};

const getExplicitHistoryColor = (entry: StatusHistoryColorFields) => {
  const value = entry.color ?? entry.status_color;
  if (!value) return null;

  const color = String(value).trim();
  return /^(#|rgb\(|rgba\(|hsl\(|hsla\(|var\()/.test(color) ? color : null;
};

const getStatusMarkerStyle = (color: string, isSelected = false) => ({
  radius: isSelected ? 14 : 11,
  color: isSelected ? '#0f172a' : color,
  fillColor: color,
  fillOpacity: isSelected ? 1 : 0.85,
  weight: isSelected ? 3 : 1.5,
});

const BOOKING_ADDRESS_MARKER_COLOR = '#0d6efd';

const getBookingAddressMarkerIcon = () =>
  window.L.divIcon({
    className: 'booking-address-marker',
    html: `
      <div style="
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: ${BOOKING_ADDRESS_MARKER_COLOR};
        color: #ffffff;
        border: 2px solid #ffffff;
        box-shadow: 0 2px 8px rgba(15, 23, 42, 0.35);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor">
          <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
        </svg>
      </div>
    `,
    iconAnchor: [16, 16],
    iconSize: [32, 32],
    popupAnchor: [0, -16],
  });

const getHistoryEntryKey = (
  entry: BookingSessionStatusHistory,
  index: number
) => `${entry.id ?? index}-${entry.created_at}-${entry.status}`;

const getHistoryStatusSettings = (
  status: string,
  statuses: StatusWithSyleClass[] = []
) => {
  const normalizedStatus = normalizeHistoryStatus(status);
  return statuses.find((candidate) => {
    const normalizedDisplay = normalizeHistoryStatus(candidate.display);
    const normalizedValue = normalizeHistoryStatus(candidate.value);

    return (
      normalizedDisplay === normalizedStatus ||
      normalizedValue === normalizedStatus
    );
  });
};

const getHistoryStatusPresentation = (
  entry: BookingSessionStatusHistory,
  statuses: StatusWithSyleClass[],
  theme: Theme
) => {
  const historyEntry = entry as StatusHistoryColorFields;
  const matchedStatus = getHistoryStatusSettings(entry.status, statuses);
  const labelColor = normalizeLabelColor(
    historyEntry.style_class ?? matchedStatus?.style_class
  );

  return {
    color:
      getExplicitHistoryColor(historyEntry) ??
      getThemeStatusColor(labelColor, theme),
    display: matchedStatus?.display ?? entry.status,
  };
};

const haversineDistanceKm = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
) => {
  const R = 6371; // km
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const toRadians = (value: number) => (value * Math.PI) / 180;

const formatDistanceKm = (distanceKm: number) => {
  if (distanceKm >= 1) {
    return `${distanceKm.toFixed(1)} km`;
  }
  return `${Math.round(distanceKm * 1000)} m`;
};

const StatusHistoryMapDialog = ({
  open,
  onClose,
  bookingAddress,
  statusHistory,
  bookingSessionStatuses = [],
}: StatusHistoryMapDialogProps) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const statusMarkersRef = useRef<Record<string, any>>({});
  const hasPositionedInitialMapRef = useRef(false);
  const [isLeafletReady, setIsLeafletReady] = useState(false);
  const [selectedStatusPointKey, setSelectedStatusPointKey] = useState<
    string | null
  >(null);

  const bookingLocation = useMemo(() => {
    if (!bookingAddress) return null;
    const lat = parseCoordinate(bookingAddress.latitude);
    const lng = parseCoordinate(bookingAddress.longitude);
    if (lat === null || lng === null) return null;
    return {
      lat,
      lng,
      label: bookingAddress.formatted_address || bookingAddress.postcode,
    };
  }, [bookingAddress]);

  const routeHistoryRows = useMemo(
    () =>
      statusHistory.map((entry, index) => {
        const lat = parseCoordinate(entry.latitude);
        const lng = parseCoordinate(entry.longitude);
        const statusPresentation = getHistoryStatusPresentation(
          entry,
          bookingSessionStatuses,
          theme
        );
        const hasCoords = lat !== null && lng !== null;
        const distanceLabel =
          hasCoords && bookingLocation
            ? formatDistanceKm(
                haversineDistanceKm(
                  bookingLocation.lat,
                  bookingLocation.lng,
                  lat as number,
                  lng as number
                )
              )
            : null;

        return {
          entry,
          key: getHistoryEntryKey(entry, index),
          lat,
          lng,
          color: statusPresentation.color,
          display: statusPresentation.display,
          distanceLabel,
          hasCoords,
        };
      }),
    [bookingLocation, bookingSessionStatuses, statusHistory, theme]
  );

  const statusPoints = useMemo(
    () =>
      routeHistoryRows
        .filter((row) => row.hasCoords)
        .map((row) => ({
          key: row.key,
          lat: row.lat as number,
          lng: row.lng as number,
          label: `${row.display} • ${row.entry.created_at}`,
          color: row.color,
        })),
    [routeHistoryRows]
  );

  const statusLegendItems = useMemo(() => {
    const items = new Map<string, { color: string; display: string }>();

    routeHistoryRows.forEach((row) => {
      const key = normalizeHistoryStatus(row.entry.status);
      if (!key || items.has(key)) return;
      items.set(key, { color: row.color, display: row.display });
    });

    return Array.from(items.values());
  }, [routeHistoryRows]);

  useEffect(() => {
    if (!open) return;
    hasPositionedInitialMapRef.current = false;
    setSelectedStatusPointKey(null);
    ensureLeafletAssets()
      .then(() => setIsLeafletReady(true))
      .catch(() => setIsLeafletReady(false));
  }, [open]);

  useEffect(() => {
    if (!open || !isLeafletReady || mapRef.current || !containerRef.current)
      return;
    if (!window.L) return;

    const mapInstance = window.L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: false,
    }).setView(DEFAULT_LEAFLET_CENTER, 6);

    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(mapInstance);

    mapRef.current = mapInstance;

    return () => {
      mapInstance.remove();
      mapRef.current = null;
      hasPositionedInitialMapRef.current = false;
    };
  }, [open, isLeafletReady]);

  useEffect(() => {
    if (!open || !mapRef.current || !window.L) return;

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
    statusMarkersRef.current = {};

    if (bookingLocation) {
      const marker = window.L.marker(
        [bookingLocation.lat, bookingLocation.lng],
        { icon: getBookingAddressMarkerIcon() }
      ).addTo(mapRef.current);
      marker.bindTooltip(`Booking address • ${bookingLocation.label}`, {
        direction: 'top',
        offset: [0, -8],
      });
      markersRef.current.push(marker);
    }

    statusPoints.forEach((point) => {
      const marker = window.L.circleMarker(
        [point.lat, point.lng],
        getStatusMarkerStyle(point.color, point.key === selectedStatusPointKey)
      ).addTo(mapRef.current);
      marker.bindTooltip(point.label, {
        direction: 'top',
        offset: [0, -8],
      });
      marker.on('click', () => setSelectedStatusPointKey(point.key));
      markersRef.current.push(marker);
      statusMarkersRef.current[point.key] = marker;
    });

    if (hasPositionedInitialMapRef.current) return;

    if (markersRef.current.length) {
      const bounds = window.L.latLngBounds(
        markersRef.current.map((marker) => marker.getLatLng())
      );
      mapRef.current.fitBounds(bounds, { padding: [32, 32], maxZoom: 16 });
      hasPositionedInitialMapRef.current = true;
    } else {
      mapRef.current.setView(DEFAULT_LEAFLET_CENTER, 6);
    }
  });

  useEffect(() => {
    if (!selectedStatusPointKey || !mapRef.current) return;
    const marker = statusMarkersRef.current[selectedStatusPointKey];
    const latLng = marker?.getLatLng?.();

    if (latLng) {
      mapRef.current.panTo(latLng, { animate: true, duration: 0.5 });
    }

    marker?.openTooltip?.();
  }, [isLeafletReady, selectedStatusPointKey, statusPoints]);

  useEffect(() => {
    if (open && mapRef.current) {
      setTimeout(() => {
        mapRef.current.invalidateSize?.();
      }, 120);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Session route on map</DialogTitle>
      <DialogContent dividers>
        <Box
          ref={containerRef}
          sx={{
            height: 400,
            width: '100%',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        />
        {!bookingLocation && !statusPoints.length && (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 2 }}
          >
            No coordinates available to display on the map.
          </Typography>
        )}
        {(bookingLocation || statusLegendItems.length > 0) && (
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
            {bookingLocation && (
              <RouteLegendItem
                color={BOOKING_ADDRESS_MARKER_COLOR}
                icon="house"
                label="Booking address"
              />
            )}
            {statusLegendItems.map((item) => (
              <RouteLegendItem
                key={`${item.display}-${item.color}`}
                color={item.color}
                label={item.display}
              />
            ))}
          </Stack>
        )}
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Status history records</Typography>
          <List
            dense
            sx={{
              maxHeight: 220,
              overflow: 'auto',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              py: 0,
            }}
          >
            {routeHistoryRows.map((row, index) => {
              const isSelected = row.key === selectedStatusPointKey;

              return (
                <ListItemButton
                  key={row.key}
                  aria-label={`Select ${row.display} status location from ${row.entry.created_at}`}
                  aria-pressed={isSelected}
                  selected={isSelected}
                  disabled={!row.hasCoords}
                  onClick={() => setSelectedStatusPointKey(row.key)}
                  sx={{
                    alignItems: 'flex-start',
                    borderBottom: (theme) =>
                      index === routeHistoryRows.length - 1
                        ? 'none'
                        : `1px solid ${theme.palette.divider}`,
                    gap: 1.25,
                    cursor: row.hasCoords ? 'pointer' : 'default',
                    '&.Mui-disabled': {
                      opacity: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: row.color,
                      boxShadow: isSelected
                        ? `0 0 0 3px ${row.color}33`
                        : 'none',
                      flexShrink: 0,
                      mt: 0.75,
                    }}
                  />
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ minWidth: 0, width: 1 }}
                  >
                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {row.display}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.entry.created_at} •{' '}
                        {row.entry.full_name || 'Unknown'}{' '}
                        {row.entry.type ? `(${row.entry.type})` : ''}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="caption"
                      color={row.hasCoords ? 'text.secondary' : 'text.disabled'}
                      sx={{ flexShrink: 0, pt: { sm: 0.25 } }}
                    >
                      {row.distanceLabel
                        ? `${row.distanceLabel} from property`
                        : row.hasCoords
                        ? 'Coordinates available'
                        : 'No coordinates'}
                    </Typography>
                  </Stack>
                </ListItemButton>
              );
            })}
          </List>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const RouteLegendItem = ({
  color,
  icon,
  label,
}: {
  color: string;
  icon?: 'house';
  label: string;
}) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    {icon === 'house' ? (
      <Iconify
        icon="eva:home-fill"
        width={14}
        height={14}
        sx={{ color, flexShrink: 0 }}
      />
    ) : (
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: color,
          flexShrink: 0,
        }}
      />
    )}
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Stack>
);
